import classNames from 'classnames/bind';

import { Button, type UnstyledButtonProps } from '@/components/shared/buttons/Button';
import { Icon } from '@/components/shared/Icon';
import { Loader } from '@/components/shared/loaders/Loader';
import type { IconName } from '@/types/shared';

import style from './ButtonIcon.module.sass';

const cx = classNames.bind(style);

export type ButtonIconVariant = 'primary' | 'secondary' | 'primaryGhost' | 'dangerGhost' | 'light';

export type ButtonIconProps = {
  isActive?: boolean;
  onClick?: UnstyledButtonProps['onClick'];
  iconName: IconName;
  className?: string;
  variant?: ButtonIconVariant;
  iconClassName?: string;
  ariaLabel?: string;
  disabled?: boolean;
  disabledVariant?: 'styled' | 'unstyled';
  isBusy?: boolean;
  'data-testid'?: string;
  size?: 'large' | 'normal' | 'small';
};

export const ButtonIcon = ({
  isActive,
  onClick,
  iconName,
  className,
  iconClassName,
  ariaLabel,
  disabled,
  disabledVariant = 'styled',
  isBusy,
  variant = 'primaryGhost',
  size = 'normal',
  'data-testid': dataTestId,
}: ButtonIconProps) => {
  const buttonClassName = cx('button', variant, size, className, {
    isActive,
    disabled: disabled && disabledVariant === 'styled',
    disabledUnstyled: disabled && disabledVariant === 'unstyled',
  });

  const iconClassNames = cx('icon', iconClassName);

  return (
    <Button.Unstyled
      data-testid={dataTestId}
      onClick={onClick}
      className={buttonClassName}
      disabled={isBusy || disabled}
      ariaLabel={ariaLabel}>
      {isBusy ? <Loader size='small' /> : <Icon name={iconName} className={iconClassNames} />}
    </Button.Unstyled>
  );
};
